import React, { useId, useState } from 'react';
import {
  Alert,
  Button,
  Flex,
  Form,
  InputNumber,
  Segmented,
  Switch,
} from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import {
  CustomDatePicker,
  CustomInput,
  CustomParagraph,
  CustomSelect,
} from '@/components';
import { RightTailArrowIcon } from '@/assets/icons/icons';
import {
  getPickupErrorMessage,
  type FedExPickupPrefill,
  type PickupCarrierType,
  type PickupPackageLocation,
} from '@/apis/fedexPickup';
import {
  MAX_PACKAGE_COUNT,
  isValidUsPhone,
  to12Hour,
} from './fedexPickupUtils';
import {
  PickupField,
  PickupSectionTitle,
  PickupTextInput,
  pickupInputProps,
} from './PickupFormField';
import { usePickupForm } from './usePickupForm';

const LOCATION_OPTIONS: { value: PickupPackageLocation; label: string }[] = [
  { value: 'FRONT', label: 'Front' },
  { value: 'REAR', label: 'Rear' },
  { value: 'SIDE', label: 'Side' },
  { value: 'NONE', label: 'None' },
];

export const EXPRESS_RESIDENTIAL_MESSAGE =
  'FedEx Express cannot pick up from residential addresses; use Ground';
export const GROUND_RESIDENTIAL_MESSAGE =
  'FedEx may add a residential surcharge';

const DATE_FORMAT = 'YYYY-MM-DD';

interface SchedulePickupPanelProps {
  shipmentId: string;
  shipmentLabel?: string;
  prefill: FedExPickupPrefill;
  onBack: () => void;
  onClose: () => void;
  onScheduled: (confirmationCode: string) => void;
}

const SchedulePickupPanel: React.FC<SchedulePickupPanelProps> = ({
  shipmentId,
  shipmentLabel,
  prefill,
  onBack,
  onClose,
  onScheduled,
}) => {
  const { t } = useTranslation('shipments');
  const idPrefix = useId();
  const id = (name: string) => `${idPrefix}-${name}`;

  const form = usePickupForm(shipmentId, prefill, onScheduled);
  const {
    timing,
    isScheduling,
    carrierType,
    handleCarrierChange,
    address,
    patchAddress,
    contact,
    setContact,
    packageCount,
    setPackageCount,
    packageLocation,
    setPackageLocation,
    remarks,
    setRemarks,
    error,
    expressResidential,
    residentialBlocked,
    valid,
    handleSubmit,
  } = form;
  const {
    availability,
    option,
    bounds,
    pickupDate,
    adjustedTo,
    readyTime,
    setReadyTime,
    closeTime,
    setCloseTime,
    handleDateChange,
    notAvailable,
    dateAdjustedAway,
  } = timing;

  const timeOptions = (times?: string[]) =>
    (times ?? []).map((time) => ({ value: time, label: to12Hour(time) }));
  const timePlaceholder = availability.isFetching
    ? t('pickup.loading', 'Loading…')
    : t('pickup.selectTime', 'Select time');
  const [showMissing, setShowMissing] = useState(false);
  const requiredText = t('pickup.required', 'Required');
  const missing = (isMissing: boolean) =>
    showMissing && isMissing ? requiredText : undefined;
  const phoneError = contact.phone
    ? isValidUsPhone(contact.phone)
      ? undefined
      : t('pickup.phoneInvalid', 'Enter a 10-digit US phone number')
    : missing(true);
  const closeError =
    readyTime && closeTime && readyTime >= closeTime
      ? t('pickup.closeBeforeReady', 'Must be after the ready time')
      : missing(!closeTime);
  const hasFieldError = [
    phoneError,
    closeError,
    missing(!readyTime),
    missing(!packageCount),
    missing(!contact.name.trim() && !contact.company.trim()),
  ].some(Boolean);
  const showIncompleteAlert =
    showMissing &&
    !valid &&
    !hasFieldError &&
    !residentialBlocked &&
    !notAvailable &&
    !availability.isError;
  const onSubmitClick = () => {
    if (valid) {
      const _ = handleSubmit();
    } else {
      setShowMissing(true);
    }
  };

  return (
    <Flex vertical style={{ minHeight: '100%' }}>
      <Flex justify='space-between' align='center' style={{ padding: '4px 0' }}>
        <Flex align='center' gap={8}>
          <span className='c-pointer' onClick={onBack} title='Back'>
            <RightTailArrowIcon />
          </span>
          <span className='pickup-panel-shipment'>
            {t('pickup.shipment', 'Shipment')}{' '}
            <strong>{shipmentLabel ?? ''}</strong>
          </span>
        </Flex>
        <span className='c-pointer' onClick={onClose} title='Close'>
          <CloseOutlined style={{ fontSize: 14 }} />
        </span>
      </Flex>
      <div className='pickup-panel-body'>
        <Form layout='vertical' requiredMark>
          <Flex vertical gap={24}>
            <CustomParagraph
              text={t('pickup.panelTitle', 'Schedule pickup')}
              fontSize={24}
              fontWeight='700'
              marginTop='16px'
              marginBottom='0'
            />
            <Flex vertical gap={8}>
              <PickupField
                htmlFor={id('from')}
                label={t('pickup.pickupFrom', 'Pickup from')}
              >
                <Flex
                  id={id('from')}
                  align='flex-start'
                  justify='space-between'
                  gap={12}
                  style={{
                    border: '1px solid var(--border-color, #e2e2e2)',
                    borderRadius: 8,
                    padding: '12px 12px 12px 16px',
                    fontSize: 12,
                    lineHeight: '20px',
                    color: 'var(--text-secondary)',
                  }}
                >
                  <div>
                    {contact.company && (
                      <div className='pickup-panel-company'>
                        {contact.company}
                      </div>
                    )}
                    <div>{address.street}</div>
                    <div>
                      {[address.city, address.state].filter(Boolean).join(', ')}{' '}
                      {address.postalCode}
                    </div>
                  </div>
                  <Flex align='center' gap={12}>
                    <label
                      htmlFor={id('residential')}
                      className='pickup-panel-residential-label'
                    >
                      {t('pickup.residential', 'Residential address')}
                    </label>
                    <Switch
                      id={id('residential')}
                      size='small'
                      checked={address.residential}
                      onChange={(residential) => patchAddress({ residential })}
                    />
                  </Flex>
                </Flex>
              </PickupField>
              <Segmented<PickupCarrierType>
                block
                value={carrierType}
                onChange={handleCarrierChange}
                options={[
                  { value: 'GROUND', label: 'FedEx Ground' },
                  { value: 'EXPRESS', label: 'FedEx Express' },
                ]}
              />
              {carrierType === 'GROUND' && address.residential && (
                <Alert
                  type='warning'
                  showIcon
                  title={t(
                    'pickup.groundResidential',
                    GROUND_RESIDENTIAL_MESSAGE
                  )}
                />
              )}
              {residentialBlocked && (
                <Alert
                  type='error'
                  showIcon
                  title={
                    expressResidential
                      ? t(
                          'pickup.expressResidential',
                          EXPRESS_RESIDENTIAL_MESSAGE
                        )
                      : t(
                          'pickup.residentialUnavailable',
                          'FedEx cannot pick up from this residential address on the selected date'
                        )
                  }
                />
              )}
            </Flex>

            <PickupSectionTitle
              text={t('pickup.sectionSchedule', 'Schedule')}
              heading
            />
            <PickupField
              htmlFor={id('date')}
              required
              label={t('pickup.date', 'Pickup date')}
            >
              <CustomDatePicker
                id={id('date')}
                value={dayjs(pickupDate)}
                allowClear={false}
                placeholder={t('pickup.enterDate', 'Enter date')}
                timeZone={prefill.timezone}
                disabledDate={(d) =>
                  d.format(DATE_FORMAT) < bounds.min ||
                  d.format(DATE_FORMAT) > bounds.max
                }
                onChange={(d) => {
                  const picked = Array.isArray(d) ? d[0] : d;
                  if (picked) handleDateChange(picked.format(DATE_FORMAT));
                }}
                style={{ width: '100%', height: 36 }}
              />
            </PickupField>
            <Flex vertical gap={8}>
              <Flex gap={16}>
                <PickupField
                  htmlFor={id('ready')}
                  required
                  label={t('pickup.readyBy', 'Ready by')}
                  error={missing(!readyTime)}
                >
                  <CustomSelect
                    id={id('ready')}
                    placeholder={timePlaceholder}
                    value={readyTime || undefined}
                    onChange={(v) => setReadyTime(v as string)}
                    loading={availability.isFetching}
                    options={timeOptions(option?.readyTimeOptions)}
                  />
                </PickupField>
                <PickupField
                  htmlFor={id('close')}
                  required
                  label={t('pickup.closesAt', 'Closes at')}
                  error={closeError}
                >
                  <CustomSelect
                    id={id('close')}
                    placeholder={timePlaceholder}
                    value={closeTime || undefined}
                    onChange={(v) => setCloseTime(v as string)}
                    loading={availability.isFetching}
                    options={timeOptions(option?.latestTimeOptions)}
                  />
                </PickupField>
              </Flex>
              {option?.available && option.cutOffTime && (
                <CustomParagraph
                  text={t('pickup.cutoff', 'Cutoff time: {{time}}', {
                    time: to12Hour(option.cutOffTime),
                  })}
                  fontSize={10}
                  color='var(--text-secondary)'
                  marginBottom='0'
                />
              )}
              {adjustedTo && adjustedTo === pickupDate && (
                <Alert
                  type='info'
                  showIcon
                  title={t(
                    'pickup.nextAvailable',
                    "FedEx's next available pickup is {{date}}",
                    { date: adjustedTo }
                  )}
                />
              )}
              {dateAdjustedAway && (
                <Alert
                  type='warning'
                  showIcon
                  title={t(
                    'pickup.nextAvailableOutside',
                    "FedEx's next available pickup is {{date}}, outside the allowed window for this carrier type",
                    { date: option?.pickupDate }
                  )}
                />
              )}
              {notAvailable && (
                <Alert
                  type='warning'
                  showIcon
                  title={t(
                    'pickup.notAvailable',
                    'FedEx pickup is not available for this date and address'
                  )}
                />
              )}
              {availability.isError && (
                <Alert
                  type='error'
                  showIcon
                  title={getPickupErrorMessage(availability.error)}
                />
              )}
            </Flex>

            <PickupSectionTitle
              text={t('pickup.sectionContact', 'Contact')}
              heading
            />
            <Flex gap={16}>
              <PickupField
                htmlFor={id('name')}
                required={!contact.company.trim()}
                label={t('pickup.name', 'Name')}
                error={missing(!contact.name.trim() && !contact.company.trim())}
              >
                <PickupTextInput
                  id={id('name')}
                  value={contact.name}
                  placeholder={t('pickup.enterName', 'Enter Name')}
                  onChange={(name) => setContact({ ...contact, name })}
                />
              </PickupField>
              <PickupField
                htmlFor={id('phone')}
                required
                label={t('pickup.phoneNumber', 'Phone number')}
                error={phoneError}
              >
                <CustomInput
                  id={id('phone')}
                  value={contact.phone}
                  prefix='+1'
                  onChange={(e) =>
                    setContact({ ...contact, phone: e.target.value })
                  }
                  {...pickupInputProps}
                />
              </PickupField>
            </Flex>

            <PickupSectionTitle
              text={t('pickup.sectionPackage', 'Package')}
              heading
            />
            <Flex gap={16}>
              <PickupField
                htmlFor={id('count')}
                required
                label={t('pickup.quantity', 'Quantity')}
                error={missing(!packageCount)}
              >
                <InputNumber
                  id={id('count')}
                  min={1}
                  max={MAX_PACKAGE_COUNT}
                  precision={0}
                  value={packageCount}
                  onChange={(v) =>
                    setPackageCount(v == null ? null : Number(v))
                  }
                  style={{
                    width: '100%',
                    height: 36,
                    border: 0,
                    borderRadius: 10,
                    background: 'var(--light-gray)',
                  }}
                />
              </PickupField>
              <PickupField
                htmlFor={id('location')}
                label={t('pickup.packageLocation', 'Package location')}
              >
                <CustomSelect
                  id={id('location')}
                  value={packageLocation}
                  onChange={(v) =>
                    setPackageLocation(v as PickupPackageLocation)
                  }
                  options={LOCATION_OPTIONS}
                />
              </PickupField>
            </Flex>
            <PickupField
              htmlFor={id('remarks')}
              label={t('pickup.remarks', 'Remarks')}
            >
              <CustomInput
                id={id('remarks')}
                maxLength={60}
                value={remarks}
                placeholder={t(
                  'pickup.remarksPlaceholder',
                  'Optional notes to courier'
                )}
                onChange={(e) => setRemarks(e.target.value)}
                {...pickupInputProps}
              />
            </PickupField>
            {showIncompleteAlert && (
              <Alert
                type='error'
                showIcon
                title={t(
                  'pickup.incomplete',
                  'Pickup details are incomplete or still loading. Check the form and try again.'
                )}
              />
            )}
            {error && <Alert type='error' showIcon title={error} />}
          </Flex>
        </Form>
      </div>
      <Flex
        vertical
        gap={8}
        style={{
          position: 'sticky',
          bottom: 0,
          padding: '16px 0 24px',
          background: 'var(--surface-container)',
        }}
      >
        <Button
          type='primary'
          block
          loading={isScheduling}
          onClick={onSubmitClick}
          style={{
            height: 48,
            fontWeight: 600,
            fontSize: 16,
            borderRadius: 8,
            background: '#000',
          }}
        >
          {t('pickup.panelSubmit', 'Schedule pickup')}
        </Button>
        <Button type='text' block onClick={onBack} style={{ height: 48 }}>
          {t('pickup.cancel', 'Cancel')}
        </Button>
      </Flex>
    </Flex>
  );
};

export default SchedulePickupPanel;
